import React, { Fragment, useContext } from 'react';
import classNames from 'classnames';
import { Button, Icon, Link } from '@/components/shared';
import { DrawerBody } from '@/components/shared/Drawer/DrawerBody';
import { DrawerHeader } from '@/components/shared/Drawer/DrawerHeader';
import { DrawerLevel } from '@/components/shared/Drawer/DrawerLevel';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetTranslations } from '@/generated/translations';
import { stripAllWhitespace } from '@/helpers';
import { DrawerMegaMenuItem } from '@/types/drawerMegaMenuItem';
import styles from './DrawerMegaMenu.module.scss';

interface DrawerMenuLevelProps {
	items: DrawerMegaMenuItem[];
	level: number;
	parentTitle?: string;
	parentInternalLink?: string;
	menuPath: string[];
	isMenuOpen: boolean;
	handleItemActivation: (key: string) => void;
	handleBack: () => void;
	handleCloseMenu: () => void;
}

export const DrawerMenuLevel: React.FC<DrawerMenuLevelProps> = ({
	items,
	level,
	parentTitle,
	parentInternalLink,
	menuPath,
	isMenuOpen,
	handleItemActivation,
	handleBack,
	handleCloseMenu,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const isRoot = level === 1;
	const MAX_LEVEL = 3;
	const isActive = isMenuOpen && menuPath.length + 1 >= level;

	// Helper to get key for a menu item
	const getKey = (level: number, index: number, title: string) => stripAllWhitespace(`${level}-${index}-${title}`);

	return (
		<DrawerLevel isActive={isActive} isUnstyled={isRoot} zIndex={level}>
			{!isRoot ? <DrawerHeader isSubLevel handleBack={handleBack} handleClose={handleCloseMenu} /> : null}
			<DrawerBody className={classNames(!isRoot && 'u-p-xs')}>
				{!isRoot && parentTitle && (
					<h5 className={classNames(styles.itemContentWithNoChildren, 'u-group-title')}>{parentTitle}</h5>
				)}
				<ul className={styles.list} role="menu">
					{!isRoot && parentTitle && (
						<li className={styles.itemSpacer} aria-hidden={!isActive}>
							<Button
								className={styles.itemShowAllButton}
								to={parentInternalLink || ''}
								onClick={handleCloseMenu}
								variant="linkButton"
								tabIndex={!isActive ? -1 : undefined}
								aria-label={translations?.data.mobileMenu.showAllIn + ' ' + parentTitle}
								role="menuitem"
							>
								{translations?.data.mobileMenu.showAll}
							</Button>
						</li>
					)}
					{items.map((item, idx) => {
						const key = getKey(level, idx, item.name);
						const isItemActive = menuPath.includes(key);
						return (
							<Fragment key={key}>
								<li className={styles.itemSpacer} aria-hidden={!isActive}>
									{item.children && item.children.length > 0 && level < MAX_LEVEL ? (
										<Button
											className={styles.itemContentButton}
											variant="ghost"
											isFullWidth
											onClick={() => handleItemActivation(key)}
											tabIndex={!isActive ? -1 : undefined}
											role="menuitem"
										>
											<span>{item.name}</span>
											<Icon name="chevronDown" size="md" rotate="270" />
										</Button>
									) : (
										<Link
											className={styles.itemContentWithNoChildren}
											to={item.internalLink}
											linkUrl={item.externalLink}
											onClick={handleCloseMenu}
											tabIndex={!isActive ? -1 : undefined}
											role="menuitem"
											textLink
											target={item.externalLink ? '_blank' : undefined}
											hideExternalIcon
										>
											{item.name}
										</Link>
									)}
									{/* Render child level if active */}
									{item.children && item.children.length > 0 && level < MAX_LEVEL && (
										<DrawerMenuLevel
											items={item.children}
											level={level + 1}
											parentTitle={item.name}
											parentInternalLink={item.internalLink}
											menuPath={menuPath}
											isMenuOpen={isMenuOpen && isItemActive}
											handleItemActivation={handleItemActivation}
											handleBack={handleBack}
											handleCloseMenu={handleCloseMenu}
										/>
									)}
								</li>
							</Fragment>
						);
					})}
				</ul>
			</DrawerBody>
		</DrawerLevel>
	);
};
